import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Separator } from '~/components';
import { Button } from '~/components/Button';
import { FormFieldCombobox } from '~/components/FormField';
import { FormFieldRadioGroup } from '~/components/FormField/components/FormFieldRadioGroup';
import { Drawer } from '~/modules/settings/components/Drawer';
import {
  EXTERNAL_ROLES,
  LABEL_TYPES,
} from '~/modules/settings/constants/Filter';
import { useFilterForm } from '~/modules/settings/hooks/useFilterForm';
import type { LabelsFilter } from '~/modules/settings/types/filter';
import { translateOptions } from '~/modules/settings/utils/helpers';
import { SelectLabels } from '~settings/components/SelectLabels';
import { DEFAULT_FILTER_LIMIT } from '~settings/constants';

const defaultValues: LabelsFilter = {
  roles: [],
  labelType: 'all',
  parentIds: [],
};

interface UserDetailFormFilterProps {
  open: boolean;
  onApply: () => void;
}

export function UserDetailFormFilter({
  open,
  onApply,
}: UserDetailFormFilterProps) {
  const { t } = useTranslation('settings');

  const { filterForm, handleApply, handleClear } = useFilterForm<LabelsFilter>({
    onApply,
    isOpen: open,
    defaultValues,
  });

  const labelTypeOptions = useMemo(
    () =>
      LABEL_TYPES.map((labelType) => ({
        value: labelType.value,
        label: t(labelType.label),
      })),
    [t],
  );

  const labelType = filterForm.watch('labelType');
  const isParentLabel = labelType === 'parent_label';

  return (
    <FormProvider {...filterForm}>
      <Drawer.Header>
        <Drawer.Title>{t('filters')}</Drawer.Title>
      </Drawer.Header>
      <Drawer.Body>
        <FormFieldCombobox
          name="roles"
          label={t('dropdowns.role.title', { count: 2 })}
          multiselect
          displaySelectedOptions
          options={translateOptions(EXTERNAL_ROLES, t)}
          placeholder={t('dropdowns.role.placeholder', { count: 2 })}
          searchPlaceholder={t('dropdowns.role.searchPlaceholder', {
            count: 2,
          })}
        />
        <Separator className="my-md" />
        <FormFieldRadioGroup
          name="labelType"
          label={t('labelType')}
          options={labelTypeOptions}
          className="gap-md py-sm"
          onChange={() => {
            if (isParentLabel) {
              filterForm.setValue('parentIds', []);
            }
          }}
        />
        {!isParentLabel && (
          <>
            <Separator className="my-md" />
            <SelectLabels
              label={t('dropdowns.parentLabel.title', { count: 2 })}
              labelType="parent_label"
              maxSelectedOptions={DEFAULT_FILTER_LIMIT}
              name="parentIds"
              placeholder={t('dropdowns.parentLabel.placeholder', { count: 2 })}
              searchPlaceholder={t('dropdowns.parentLabel.searchPlaceholder', {
                count: 2,
              })}
              type="view"
            />
          </>
        )}
      </Drawer.Body>
      <Drawer.Footer>
        <Button
          size="large"
          variant="tertiary"
          onClick={handleClear}
          className="font-normal"
          type="button"
        >
          {t('Clear filters')}
        </Button>
        <Button
          size="large"
          type="button"
          onClick={filterForm.handleSubmit(handleApply)}
        >
          {t('Apply')}
        </Button>
      </Drawer.Footer>
    </FormProvider>
  );
}

UserDetailFormFilter.displayName = 'UserDetailFormFilter';
